import React, { useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  region?: string;
}

// Common languages used in healthcare with proper ISO codes
const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', region: 'United States' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English', flag: '🇬🇧', region: 'United Kingdom' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', region: 'Spain' },
  { code: 'es-MX', name: 'Spanish (Mexico)', nativeName: 'Español', flag: '🇲🇽', region: 'Mexico' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', region: 'France' },
  { code: 'fr-CA', name: 'French (Canada)', nativeName: 'Français', flag: '🇨🇦', region: 'Canada' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', region: 'Germany' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', region: 'Italy' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', region: 'Portugal' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português', flag: '🇧🇷', region: 'Brazil' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', region: 'Russia' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '中文', flag: '🇨🇳', region: 'China' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '中文', flag: '🇹🇼', region: 'Taiwan' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', region: 'Japan' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', region: 'Korea' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', region: 'Saudi Arabia' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', region: 'India' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', region: 'Thailand' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', region: 'Vietnam' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', region: 'Netherlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', region: 'Sweden' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴', region: 'Norway' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', region: 'Denmark' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', region: 'Finland' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', region: 'Poland' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', region: 'Turkey' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', region: 'Israel' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷', region: 'Iran' },
];

interface LanguageSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
  allowCustom?: boolean; // Allow typing custom language codes
  showSearch?: boolean; // Show search functionality for large lists
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  value = '',
  onChange,
  label = 'Preferred Language',
  required = false,
  disabled = false,
  error,
  className = '',
  placeholder = 'Select language',
  allowCustom = true,
  showSearch = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customValue, setCustomValue] = useState('');

  // Filter languages based on search term
  const filteredLanguages = LANGUAGE_OPTIONS.filter(lang =>
    lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedLanguage = LANGUAGE_OPTIONS.find(lang => lang.code === value);

  const handleLanguageSelect = (languageCode: string) => {
    onChange?.(languageCode);
    setIsOpen(false);
    setSearchTerm('');
    setCustomValue('');
  };

  const handleCustomValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCustomValue(newValue);
    onChange?.(newValue);
  };

  const handleCustomValueBlur = () => {
    // Validate the custom language code format (basic ISO 639-1 or 639-1 with region)
    if (customValue && !/^[a-z]{2}(-[A-Z]{2})?$/.test(customValue)) {
      // Show warning but don't prevent saving
      console.warn('Language code should follow ISO 639-1 format (e.g., "en" or "en-US")');
    }
  };

  const getDisplayValue = () => {
    if (selectedLanguage) {
      return `${selectedLanguage.flag} ${selectedLanguage.name}`;
    }
    if (value && !selectedLanguage) {
      return value; // Show custom value
    }
    return placeholder;
  };

  const isCustomValue = value && !selectedLanguage;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Selected Value Display / Dropdown Trigger */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full px-3 py-2 text-left border rounded-md
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
            ${!value ? 'text-gray-500' : 'text-gray-900'}
          `}
        >
          <div className="flex items-center justify-between">
            <span className="truncate">{getDisplayValue()}</span>
            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown Content */}
            <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {/* Search */}
              {showSearch && (
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search languages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* Language Options */}
              <div className="py-1">
                {filteredLanguages.map((language) => (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => handleLanguageSelect(language.code)}
                    className={`
                      w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center
                      ${value === language.code ? 'bg-primary-50 text-primary-700' : 'text-gray-900'}
                    `}
                  >
                    <span className="mr-2">{language.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{language.name}</div>
                      {language.nativeName !== language.name && (
                        <div className="text-sm text-gray-500">{language.nativeName}</div>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 ml-2">{language.code}</div>
                  </button>
                ))}

                {filteredLanguages.length === 0 && searchTerm && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No languages found matching "{searchTerm}"
                  </div>
                )}
              </div>

              {/* Custom Language Code Input */}
              {allowCustom && (
                <div className="border-t border-gray-200 p-2">
                  <div className="text-xs text-gray-600 mb-1">Enter custom language code:</div>
                  <input
                    type="text"
                    placeholder="e.g., en, fr, es-MX"
                    value={customValue}
                    onChange={handleCustomValueChange}
                    onBlur={handleCustomValueBlur}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Use ISO 639-1 format (e.g., "en" or "en-US")
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Custom Value Warning */}
      {isCustomValue && (
        <p className="text-xs text-orange-600">
          Custom language code: "{value}". Ensure this follows ISO 639-1 format.
        </p>
      )}

      {/* Selected Language Info */}
      {selectedLanguage && (
        <p className="text-xs text-gray-600">
          Language: {selectedLanguage.nativeName} ({selectedLanguage.code})
          {selectedLanguage.region && ` • ${selectedLanguage.region}`}
        </p>
      )}
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      {/* Help text */}
      {!error && !value && (
        <p className="text-xs text-gray-500">
          Select the patient's preferred language for communication
        </p>
      )}
    </div>
  );
};